import { useSelector } from 'react-redux';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { RootState } from 'types';
import { SearchType, SortOption } from 'types/domain/search';
import { ISearchReducer, selectSearchResult, updateSearch } from 'core/slices/search';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from 'core/store/hooks';
import { Sort } from '../Sort';
import { ReportType } from 'core/slices/report';
import { LoadingState } from 'types/domain/base';
import { LoadingSpinner } from '../Loading/loadingspinner';
import { ITextSearchDAO } from 'types/domain/text';
import { getImageFromFiles } from 'core/selectors/product/scene/scenePreview';
import { IModuleDAO } from 'types/domain/module';
import { XMarkIcon } from '@heroicons/react/24/outline';
import RenderHtml from '../TextEditor/Slate/renderHtml';
import { searchCourseModules } from 'core/selectors/admin/courseModules';

export function Search({
  className,
  inputClassName,
  placeholder,
  showSort = true,
  searchType,
  sortOptions,
  reportType,
  showResults,
  
}: {
  className?: string;
  inputClassName?: string;
  placeholder?: string | ReactNode;
  showSort?: boolean;
  searchType?: SearchType;
  reportType?: ReportType;
  sortOptions?: SortOption[];
  showResults?: boolean;
}) {
  const search: ISearchReducer = useSelector((state: RootState) => state.core.search);
  const modules: ITextSearchDAO[] = useSelector(searchCourseModules);

  const [term, setTerm] = useState('');
  const [open, setOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const onChange = useCallback(
    e => {
      setTerm(e.target.value || '');
      dispatch(updateSearch({ term: e.target.value || '', searchType, reportType }));
    },
    [dispatch, reportType, searchType],
  );

  const onSort = useCallback(
    sort => dispatch(updateSearch({ sort, term, searchType: searchType, reportType })),
    [dispatch, reportType, searchType, term],
  );

  const clearSearch = useCallback(() => {
    setTerm('');
    dispatch(updateSearch({ term: '', searchType, reportType }));
  }, [dispatch, reportType, searchType]);

  useEffect(() => {
  
    if (search.term?.length) dispatch(updateSearch({ term: '', searchType, reportType }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchType, reportType]);

  useEffect(() => {
    if (modules.length && !open && showResults) setOpen(true);
    else setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules.length, showResults]);

  const onBlur = e => {
    e.preventDefault();
    setOpen(false);
  };

  const onFocus = e => {
    e.preventDefault();
    if (modules.length && !open && showResults) setOpen(true);
  };

  return (
    <div className="flex">
      <div
        onBlur={onBlur}
        onFocus={onFocus}
        className={` ${className} dark:bg-howio-700 bg-white  relative flex w-full border h-10 rounded-lg`}
      >
        <div className={`flex w-full`}>
          <input
            className={`${inputClassName} h-full w-full md:min-w-60 pl-11 bg-transparent text-base shadow-sm placeholder-gray-400 focus:ring-0 sm:text-sm`}
            //placeholder={placeholder}
            value={term}
            name={'search'}
            onChange={onChange}
          ></input>
          {search.loading === LoadingState.LOADING ? (
            <LoadingSpinner className="absolute right-5 top-1/2 transform -translate-y-1/2 w-5 h-5 items-center self-center" />
          ) : search.term ? (
            <XMarkIcon
              onClick={clearSearch}
              className={
                'absolute right-5 top-1/2 transform -translate-y-1/2 w-5 h-5 items-center self-center pointer-events-auto'
              }
            />
          ) : null}
        </div>
        <MagnifyingGlassIcon
          className="pointer-events-none absolute  top-1/2 transform -translate-y-1/2 left-4 h-5 w-5 text-gray-400"
          aria-hidden="true"
        />

        {open && (
          <div className="absolute h-96 w-full flex flex-col top-full z-front overflow-auto rounded-md overflow-y-auto scroll-bar">
            {modules.length
              ? modules.map((result: ITextSearchDAO, ind) => <SearchResult key={ind} result={result} index={ind} />)
              : null}
          </div>
        )}
      </div>
      {showSort && <Sort className={'flex w-full mx-5 dark:text-white'} onSort={onSort} options={sortOptions} />}
    </div>
  );
}

function SearchResult({ result, index }: { result: ITextSearchDAO; index: number }) {
  const module: IModuleDAO = result.module as IModuleDAO;
  const imageUrl = module && module.image ? getImageFromFiles(module.image.files) : null;
  const dispatch = useAppDispatch();
  const onClick = useCallback(
    e => {
      e.stopPropagation();

      dispatch(
        selectSearchResult({
          moduleId: result.moduleId,
          actionId: result.actionId,
          index,
          created: new Date().getTime(),
        }),
      );
    },
    [dispatch, index, result.actionId, result.moduleId],
  );

  return (
    <div
      onMouseDown={onClick}
      className="w-full bg-howio-700 border-2 rounded flex cursor-pointer hover:border-brand p-2"
    >
      {imageUrl && (
        <div
          className="w-24 h-24 rounded flex-shrink-0"
          style={{
            backgroundImage: `url(${imageUrl})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        />
      )}
      <div>
        <h1 className="text-white p-2">{module?.text?.shortText || module?.name || result?.shortText}</h1>

        <RenderHtml htmlJson={result.htmlJson} className={'max-h-24 w-full text-sm overflow-hidden p-2 text-white'} />
      </div>
    </div>
  );
}
